import React, { useEffect } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid2, Box } from '@mui/material'
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import CustomButton from '@components/button/customButton';
import ControlledTextField from '@components/inputs/controlledTextField';
import ControlledAutocomplete from '@components/inputs/controlledAutocomplete';

import { useSnackbar } from '@context/snackbarContext';

import { AdminApiEndpoints } from '@configuration/apiEndpoints';

import { QUESTION_TYPE } from '@utils/constant';

const schema = z.object({
  question: z.object({
    questionGuid: z.string(),
    questionNumber: z.string(),
    title: z.string(),
    questionEng: z.string(),
    questionType: z.object({
      parameterGuid: z.string(),
      valueEng: z.string(),
    }),
    commonType: z.string(),
    maxAllowed: z.number().nullable(),
    questionOptions: z.array(
      z.object({
        optionCnsimp: z.string(),
        optionCntrad: z.string(),
        optionEng: z.string(),
        subText: z.boolean().optional()
      })
    ),
  }, {message: 'Question is required'}),
  sequence: z.string().optional().nullable(),
  data: z.union([
    z.string().optional().nullable(),
    z.number().optional().nullable(),
  ]),
});


export default function AddEditQuestionDialog({
  open,
  onClose,
  type,

  pageAction,

  questionListing,
  setQuestionListing,
  selectedQuestion,
}) {

  const questionOptions = useQuery({
    queryKey: ['getQuestionList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getQuestionList),
    initialData: [],
    select: (res) => {
      return res?.data || []
    },
  })

  // console.log(questionOptions.data)

  const { control, handleSubmit, reset, getValues, watch } = useForm({
    resolver: zodResolver(schema),
  })

  useEffect(()=>{
    if(open === true && selectedQuestion && (type === "Edit" || type === "View")){
      reset({...selectedQuestion,
        sequence: selectedQuestion.sequence.toString(),
        question: {
          questionGuid: selectedQuestion.questionGuid,
          questionNumber: selectedQuestion.questionNumber,
          title: selectedQuestion.title,
          questionEng: selectedQuestion.questionEng,
          questionType: selectedQuestion.questionType,
          commonType: selectedQuestion.commonType,
          maxAllowed: selectedQuestion.maxAllowed,
          questionOptions: selectedQuestion.questionOptions,
        }
      })
    }
  },[type, selectedQuestion, open])

  const onSubmit = (data) => {
    const {question, sequence, data: dataIndex} = data
    const dataIndexValue = [QUESTION_TYPE.SINGLE_ANSWER, QUESTION_TYPE.MULTI_OPTION_SINGLE_CHOICE].includes(question?.questionType?.valueEng) ? dataIndex : null
    if(type === "Add"){
      setQuestionListing((prev) => {
        const questionNumber = prev.length;
      
        // Ensure sequence is treated as a number
        const parsedSequence = parseInt(sequence, 10);
        let questionSequence = 1;
      
        if (isNaN(parsedSequence) || parsedSequence > questionNumber || parsedSequence <= 0) {
          // If the sequence is invalid or out of bounds, place the question at the end
          questionSequence = questionNumber + 1;
          return [...prev, { ...question, sequence: questionSequence, data: dataIndexValue }];
        } else {
          // Insert question at the correct sequence and adjust others
          const updatedList = prev.map((q) =>
            parseInt(q.sequence, 10) >= parsedSequence
              ? { ...q, sequence: parseInt(q.sequence, 10) + 1 }
              : q
          );
      
          // Insert the new question
          const newQuestion = { ...question, sequence: parsedSequence, data: dataIndexValue };
          return [...updatedList.slice(0, parsedSequence - 1), newQuestion, ...updatedList.slice(parsedSequence - 1)];
        }
      });
      handleOnClose()
    }
    if(type === "Edit"){
      setQuestionListing((prev) => {
        const questionNumber = prev.length;
      
        // Determine the updated sequence, defaulting to the existing sequence if not provided
        let updatedSequence = parseInt(sequence || prev.find((q) => q.questionGuid === question.questionGuid)?.sequence, 10);
      
        // Clamp the sequence to valid bounds
        updatedSequence = isNaN(updatedSequence) ? 1 : Math.min(Math.max(updatedSequence, 1), questionNumber);
      
        // Find the index of the question to edit
        const questionIndex = prev.findIndex((q) => q.questionGuid === question.questionGuid);
        if (questionIndex === -1) {
          console.error("Question not found");
          return prev; // Return original state if question not found
        }
      
        const existingQuestion = prev[questionIndex];
      
        // If sequence hasn't changed, update question details only
        if (updatedSequence === parseInt(existingQuestion.sequence, 10)) {
          return prev.map((q) =>
            q.questionGuid === question.questionGuid ? { ...q, data: dataIndexValue } : q
          );
        }
      
        // Remove the question being edited
        const withoutEdited = prev.filter((q) => q.questionGuid !== question.questionGuid);
      
        // Adjust other sequences based on the new position
        const adjustedList = withoutEdited.map((q) => {
          const currentSequence = parseInt(q.sequence, 10);
          if (currentSequence >= updatedSequence && currentSequence < parseInt(existingQuestion.sequence, 10)) {
            return { ...q, sequence: currentSequence + 1 }; // Move down
          }
          if (currentSequence <= updatedSequence && currentSequence > parseInt(existingQuestion.sequence, 10)) {
            return { ...q, sequence: currentSequence - 1 }; // Move up
          }
          return q; // No change
        });
      
        // Insert the updated question with the new sequence
        const updatedQuestion = { ...existingQuestion, sequence: updatedSequence, data: dataIndexValue };
        return [
          ...adjustedList.slice(0, updatedSequence - 1),
          updatedQuestion,
          ...adjustedList.slice(updatedSequence - 1),
        ];
      });    
      handleOnClose()
    }
  };

  const handleOnClose = () => {
    reset({
      question: null,
      sequence: "",
      data: ""
    })
    onClose()
  }

  const watchQuestion = watch('question')
  const watchData = watch('data')

  const getFilteredQuestions = () => {
    const addedQuestions = questionListing.map((data) => data?.questionGuid);
    return questionOptions.data.filter((item) => !addedQuestions.includes(item?.questionGuid) || item.questionGuid === watchQuestion?.questionGuid);
  };
  
  const getFilteredData = () => {
    const addedData = questionListing.map((data) => data?.data); // Extract data from questionListing
    return ["1", "2", "3", "4", "5"].filter((item) => !addedData.includes(item) || item === watchData?.data);
  };

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{ '& .MuiDialog-paper': { borderRadius: '10px' } }}
      fullWidth
    >
      <DialogTitle id="alert-dialog-title" fontSize={"0.9rem"} fontWeight={600} color='#202020' sx={{padding: '20px 25px'}}>
        {type} Question
      </DialogTitle>
      <DialogContent sx={{padding: '0px 25px', overflow: 'visible'}}>
        <Grid2 container spacing={"20px"}>
          <Grid2 size={12}>
            <ControlledAutocomplete 
              name='question'
              label='Question'
              control={control}
              options={getFilteredQuestions()}
              getOptionLabel={(option) => option.questionEng}
              getOptionKey={(option) => option.questionGuid}
              isOptionEqualToValue={(option, value) => option.questionGuid === value?.questionGuid}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                    style={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start'}}
                  >
                    <Typography fontSize={"11px"} color="#202020">{option?.questionNumber} - {option?.title}</Typography>
                    <Typography fontSize={"11px"} color="#5D5D5D">{option?.questionType?.valueEng}</Typography>
                    <div>
                      {option?.questionEng}
                    </div>
                  </Box>
                );
              }}
              disabled={type === "Edit"}
            />
          </Grid2>
          <Grid2 size={12}>
            <ControlledTextField 
              name='sequence'
              label='Sequence'
              control={control}
              type='number'
            />
          </Grid2>
          <Grid2 size={12}>
            <ControlledAutocomplete 
              name='data'
              label='Data'
              control={control}
              options={getFilteredData()}
              disabled={![QUESTION_TYPE.SINGLE_ANSWER, QUESTION_TYPE.MULTI_OPTION_SINGLE_CHOICE].includes(watchQuestion?.questionType?.valueEng)}
            />
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions sx={{padding: '20px 25px'}}>
        <CustomButton color='#999999' onClick={handleOnClose}>
          Cancel
        </CustomButton>
        <CustomButton onClick={handleSubmit(onSubmit)}>
          Submit
        </CustomButton>
      </DialogActions>
    </Dialog>
  )
}